import React, { useMemo, useState, useEffect, useCallback } from "react";
import Customtable from "../helper/Customtable";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";
import CommonModal from "../components/CommonModal";

const Bond = () => {
  const [licenseOptions, setLicenseOptions] = useState([]);
  const [consigneeOptions, setConsigneeOptions] = useState([]);
  // ------------------------------
  // Dynamic Form Field Config
  // ------------------------------
  const formFields = useMemo(() => [
     { type: "section", label: "Job Information" },
    { name: "license_id_fk", label: "license No", type: "select", required: true, col: 6,
      options: licenseOptions 
     },
    { name: "consignee_id_fk", label: "consignee", type: "select", col: 6,
      options: consigneeOptions
     },
    { name: "bond_number", label: "Bond Number", type: "text", col: 6 },
    { name: "bond_total_value", label: "Bond Total Value", type: "text", col: 6 },
    { type: "section", label: "Other Info" },
    { name: "bond_used_value", label: "Bond Used Value", type: "text", col: 6 },
    { name: "bond_avail_value", label: "Bond Avail Value", type: "text", col: 6 },
    { name: "bond_expiry_date", label: "Bond Expiry Date", type: "date", col: 6 },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: [
        { value: 0, label: "Active" },
        { value: 1, label: "Inactive" },
      ],
      col: 6,
    },
    { name: "remarks", label: "Remarks", type: "textarea", col: 12 },
    
  ], [licenseOptions,consigneeOptions]);

  // ------------------------------
  // Initial Form State
  // ------------------------------
  const initialFormState = useMemo(() => {
    return formFields.reduce((acc, field) => {
      acc[field.name] = "";
      return acc;
    }, { bond_id: 0 });
  }, [formFields]);

  const [formData, setFormData] = useState(initialFormState);
  const [bondList, setBondList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [showModal, setShowModal] = useState(false);

  // ------------------------------
  // Alert Helper
  // ------------------------------
  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({
      icon,
      title,
      text: text || "Something went wrong.",
      timer,
      showConfirmButton: false,
    });
  }, []);

  // ------------------------------
  // Fetch Data
  // ------------------------------
  const fetchData = useCallback(async () => {
    try {
      const response = await post("/compliance/bond/list");
      if (response.status === 200) {
        setBondList(response.data);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  }, [showAlert]);

  const fetchLicenseData = useCallback(async () => {
    try {
      const response = await post("/compliance/license/list");
      if (response.status === 200) {
        setLicenseOptions([
            { value: "", label: "Select License" },
            ...response.data.map(item => ({
              value: item.license_id,
              label: item.license_no,
            }))
          ]);
      }
    } catch (err) {
      console.log(err.message);
    }
  }, []);
  const fetchConsigneeData = useCallback(async () => {
    try {
      const response = await post("/masters/consignees/list");
      if (response.status === 200) {
         setConsigneeOptions([
            { value: "", label: "Select Consignee" },
            ...response.data.map(item => ({
              value: item.consignees_id,
              label: item.consignees_name,
            }))
          ]);
      }
    } catch (err) {
      console.log(err.message);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchLicenseData();
    fetchConsigneeData();
  }, [fetchData,fetchLicenseData,fetchConsigneeData]);

  // ------------------------------
  // Edit Handler (FIXED POSITION)
  // ------------------------------
  const handleEdit = useCallback((row) => {
    setFormData({ ...initialFormState, ...row });
    setShowModal(true);
  }, [initialFormState]);

  // ------------------------------
  // Table Columns
  // ------------------------------
  const columns = useMemo(
    () => [
      { header: "S.No", label_condition: "s_no", enableColumnFilter: false, enableSorting: false },
      {
        header: "Actions",
        cell: ({ row }) => (
          <i
            className="ri-file-edit-fill text-primary cursor-pointer me-2"
            onClick={() => handleEdit(row)}
            title="Edit"
            style={{ fontSize: "18px" }}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      { header: "License No", accessorKey: "license_no" },
      { header: "Consignee", accessorKey: "consignees_name" },
      { header: "Bond Number", accessorKey: "bond_number" },
      { header: "Bond Total Value", accessorKey: "bond_total_value" },
      { header: "Bond Used Value", accessorKey: "bond_used_value" },
      { header: "Bond Avail Value", accessorKey: "bond_avail_value" },
      { header: "Bond Expiry Date", accessorKey: "bond_expiry_date" },
      { header: "Remarks", accessorKey: "remarks" },
      { header: "Status", accessorKey: "status", label_condition: "status" },
    ],
    [handleEdit]
  );

  // ------------------------------
  // Input Change Handler
  // ------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ------------------------------
  // Close Modal
  // ------------------------------
  const handleClose = () => {
    setShowModal(false);
    setFormData(initialFormState);
  };

  // ------------------------------
  // Submit Form
  // ------------------------------
  const handleSubmit = async () => {
    for (let field of formFields.filter((f) => f.required)) {
      if (!formData[field.name]) {
        showAlert("error", "Oops...", `${field.label} is required`);
        return;
      }
    }

    try {
      const response = await post("/compliance/bond", formData);
      if (response.status === 200) {
        showAlert("success", "Saved", response.message);
        fetchData();
        handleClose();
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  };

  // ------------------------------
  // Render
  // ------------------------------
  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <div className="page-header-title">Bond</div>
          <div className="page-header-sub">Bond master linked to licenses</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
          Add Bond
        </button>
      </div>

      <div className="card basic-data-table mt-3">
        <div className="card-header">
          <h5 className="card-title mb-0">Bond List</h5>
        </div>
        <div className="card-body">
          <Customtable
            table_title="Bond List"
            columns={columns}
            data={bondList}
            isGlobalFilter={true}
            isPagination={true}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isExportButton={true}
          />
        </div>
      </div>

      <CommonModal
        show={showModal}
        title="Bond"
        formFields={formFields}
        formData={formData}
        onChange={handleChange}
        onClose={handleClose}
        onSubmit={handleSubmit}
        submitText="Save"
      />
    </>
  );
};

export default Bond;